<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ColorInput from 'dashboard/components-next/colorpicker/ColorInput.vue';
import CustomDomainSettings from './CustomDomainSettings.vue';

const props = defineProps({
  widget: { type: Object, required: true },
  pricingPlans: { type: Array, default: () => [] },
  structureLocked: { type: Boolean, default: false },
  isFetchingSslStatus: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update',
  'save',
  'saveCustomDomain',
  'refreshSslStatus',
  'sendDomainInstructions',
]);
const { t } = useI18n();

const patch = (key, value) => {
  if (props.structureLocked && key !== 'name') return;
  emit('update', { ...props.widget, [key]: value });
};

const designOptions = computed(() => props.widget.design_options || {});

const pricingEnabled = computed(
  () => !!designOptions.value.pricing_enabled
);

const priceDisplayMode = computed(
  () => designOptions.value.price_display_mode || 'total'
);

const pricingTotalLabel = computed(
  () => designOptions.value.pricing_total_label || ''
);

const pricingTotalInfo = computed(
  () => designOptions.value.pricing_total_info || ''
);

const activePricingPlans = computed(() =>
  (props.pricingPlans || []).filter(plan => plan.active !== false)
);

const patchDesignOption = (key, value) => {
  if (props.structureLocked) return;
  patch('design_options', {
    ...designOptions.value,
    [key]: value,
  });
};

const analyticsSettings = computed(() => {
  const raw = props.widget.analytics_settings || {};
  const googleRaw = raw.google_tag || raw.google_analytics || {};
  const tagIds = Array.isArray(googleRaw.tag_ids)
    ? googleRaw.tag_ids
    : googleRaw.api_key
      ? [googleRaw.api_key]
      : [];
  return {
    facebook_pixel: {
      enabled: !!raw.facebook_pixel?.enabled,
      api_key: raw.facebook_pixel?.api_key || '',
      use_account_capi: raw.facebook_pixel?.use_account_capi !== false,
    },
    google_tag: {
      enabled: !!googleRaw.enabled,
      tag_ids_text: tagIds.join(', '),
      lead_send_to: googleRaw.lead_send_to || '',
    },
  };
});

const patchAnalyticsProvider = (provider, key, value) => {
  if (props.structureLocked) return;
  const current = analyticsSettings.value;
  let nextProvider = { ...current[provider], [key]: value };

  if (provider === 'google_tag') {
    const tagIdsText =
      key === 'tag_ids_text' ? value : nextProvider.tag_ids_text || '';
    const tagIds = String(tagIdsText)
      .split(',')
      .map(id => id.trim())
      .filter(Boolean);
    nextProvider = {
      enabled: key === 'enabled' ? value : !!nextProvider.enabled,
      tag_ids: tagIds,
      api_key: tagIds[0] || '',
      lead_send_to:
        key === 'lead_send_to' ? value : nextProvider.lead_send_to || '',
    };
  }

  const next = {
    facebook_pixel: current.facebook_pixel,
    google_tag: current.google_tag,
    [provider]: nextProvider,
  };

  // Keep legacy key in sync for older readers until fully migrated.
  if (provider === 'google_tag') {
    next.google_analytics = {
      enabled: nextProvider.enabled,
      api_key: nextProvider.api_key,
    };
  }

  patch('analytics_settings', next);
};

const onUpdateCustomDomain = customDomain => {
  emit('update', { ...props.widget, custom_domain: customDomain });
  emit('saveCustomDomain', customDomain);
};

const embedSnippet = computed(() => {
  const token = props.widget.website_token;
  if (!token) return '';
  const base = window.location.origin;
  const open = '<' + 'script>';
  const close = '</' + 'script>';
  return `${open}
(function(d,t) {
  var BASE_URL="${base}";
  var g=d.createElement(t),s=d.getElementsByTagName(t)[0];
  g.src=BASE_URL+"/packs/js/bookingsdk.js";
  g.defer=true;g.async=true;
  s.parentNode.insertBefore(g,s);
  g.onload=function(){
    window.RookooBookingSDK.mount({
      baseUrl: BASE_URL,
      websiteToken: "${token}",
      containerId: "rookoo-booking-widget"
    })
  }
})(document,"script");
${close}
<div id="rookoo-booking-widget"></div>`;
});
</script>

<template>
  <section
    class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-n-solid-1"
  >
    <div
      class="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-n-weak px-6"
    >
      <div class="min-w-0 truncate text-lg font-medium text-n-slate-12">
        {{ t('BOOKING_WIDGETS.WIDGET_SETTINGS') }}
      </div>
      <Button
        md
        :label="t('BOOKING_WIDGETS.SAVE')"
        @click="emit('save')"
      />
    </div>

    <div
      class="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain bg-n-solid-1 p-6"
    >
      <div class="space-y-4 rounded-xl border border-n-weak bg-n-solid-2 p-4">
        <p
          v-if="structureLocked"
          class="rounded-lg bg-n-alpha-1 px-3 py-2 text-xs text-n-slate-11"
        >
          {{ t('BOOKING_WIDGETS.INHERITED_STRUCTURE_HELP') }}
        </p>

        <Input
          :model-value="widget.name || ''"
          :label="t('BOOKING_WIDGETS.NAME')"
          @update:model-value="patch('name', $event)"
          class="!text-base font-normal"
        />

        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('BOOKING_WIDGETS.ENABLED') }}
          </span>
          <Switch
            :model-value="!!widget.enabled"
            :disabled="structureLocked"
            @update:model-value="patch('enabled', $event)"
          />
        </div>

        <div
          class="grid grid-cols-2 gap-4"
          :class="{ 'pointer-events-none opacity-60': structureLocked }"
        >
          <ColorInput
            :model-value="widget.widget_color || ''"
            :label="t('BOOKING_WIDGETS.COLOR')"
            @update:model-value="patch('widget_color', $event)"
          />
          <ColorInput
            :model-value="widget.widget_backgroundcolor || ''"
            :label="t('BOOKING_WIDGETS.BACKGROUND')"
            @update:model-value="patch('widget_backgroundcolor', $event)"
          />
        </div>
      </div>

      <div
        v-if="!structureLocked"
        class="space-y-4 rounded-xl border border-n-weak bg-n-solid-2 p-4"
      >
        <div>
          <h3
            class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <span class="i-lucide-banknote size-4 shrink-0 text-n-slate-11" />
            {{ t('BOOKING_WIDGETS.PRICING.TITLE') }}
          </h3>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('BOOKING_WIDGETS.PRICING.HELP') }}
          </p>
        </div>

        <div class="flex items-center justify-between gap-3">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('BOOKING_WIDGETS.PRICING.ENABLED') }}
          </span>
          <Switch
            :model-value="pricingEnabled"
            @update:model-value="patchDesignOption('pricing_enabled', $event)"
          />
        </div>

        <template v-if="pricingEnabled">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ t('BOOKING_WIDGETS.PRICING.DISPLAY_MODE') }}
            <select
              class="mt-1.5 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm"
              :value="priceDisplayMode"
              @change="
                patchDesignOption('price_display_mode', $event.target.value)
              "
            >
              <option value="total">
                {{ t('BOOKING_WIDGETS.PRICING.DISPLAY_TOTAL') }}
              </option>
              <option value="per_person">
                {{ t('BOOKING_WIDGETS.PRICING.DISPLAY_PER_PERSON') }}
              </option>
              <option value="both">
                {{ t('BOOKING_WIDGETS.PRICING.DISPLAY_BOTH') }}
              </option>
            </select>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ t('BOOKING_WIDGETS.PRICING.DISPLAY_MODE_HELP') }}
            </p>
          </label>

          <Input
            :model-value="pricingTotalLabel"
            :label="t('BOOKING_WIDGETS.PRICING.TOTAL_LABEL')"
            :placeholder="t('BOOKING_WIDGETS.PRICING.TOTAL_LABEL_PLACEHOLDER')"
            @update:model-value="
              patchDesignOption('pricing_total_label', $event)
            "
          />
          <p class="-mt-2 text-xs text-n-slate-11">
            {{ t('BOOKING_WIDGETS.PRICING.TOTAL_LABEL_HELP') }}
          </p>

          <TextArea
            :model-value="pricingTotalInfo"
            :label="t('BOOKING_WIDGETS.PRICING.TOTAL_INFO')"
            :placeholder="t('BOOKING_WIDGETS.PRICING.TOTAL_INFO_PLACEHOLDER')"
            :max-length="400"
            auto-height
            min-height="3rem"
            @update:model-value="
              patchDesignOption('pricing_total_info', $event)
            "
          />
          <p class="-mt-2 text-xs text-n-slate-11">
            {{ t('BOOKING_WIDGETS.PRICING.TOTAL_INFO_HELP') }}
          </p>

          <label class="block text-sm font-medium text-n-slate-12">
            {{ t('BOOKING_WIDGETS.PRICING.PLAN') }}
            <select
              class="mt-1.5 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm"
              :value="widget.pricing_plan_id || ''"
              @change="
                patch(
                  'pricing_plan_id',
                  $event.target.value ? Number($event.target.value) : null
                )
              "
            >
              <option value="">
                {{ t('BOOKING_WIDGETS.PRICING.PLAN_DEFAULT') }}
              </option>
              <option
                v-for="plan in activePricingPlans"
                :key="plan.id"
                :value="plan.id"
              >
                {{ plan.name }}
              </option>
            </select>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ t('BOOKING_WIDGETS.PRICING.PLAN_HELP') }}
            </p>
          </label>
        </template>
      </div>

      <div
        v-if="!structureLocked"
        class="rounded-xl border border-n-weak bg-n-solid-2 p-4"
      >
        <CustomDomainSettings
          :widget="widget"
          :is-fetching-status="isFetchingSslStatus"
          @update-custom-domain="onUpdateCustomDomain"
          @refresh-status="emit('refreshSslStatus')"
          @send-cname-instructions="emit('sendDomainInstructions', $event)"
        />
      </div>

      <div
        v-if="!structureLocked"
        class="space-y-4 rounded-xl border border-n-weak bg-n-solid-2 p-4"
      >
        <div>
          <h3
            class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <span class="i-lucide-chart-column size-4 shrink-0 text-n-slate-11" />
            {{ t('BOOKING_WIDGETS.ANALYTICS.TITLE') }}
          </h3>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('BOOKING_WIDGETS.ANALYTICS.HELP') }}
          </p>
        </div>

        <div class="space-y-3 rounded-lg border border-n-weak p-3">
          <div class="flex items-center justify-between gap-3">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('BOOKING_WIDGETS.ANALYTICS.FACEBOOK_PIXEL') }}
            </span>
            <Switch
              :model-value="analyticsSettings.facebook_pixel.enabled"
              @update:model-value="
                patchAnalyticsProvider('facebook_pixel', 'enabled', $event)
              "
            />
          </div>
          <Input
            v-if="analyticsSettings.facebook_pixel.enabled"
            :model-value="analyticsSettings.facebook_pixel.api_key"
            :label="t('BOOKING_WIDGETS.ANALYTICS.FACEBOOK_PIXEL_ID')"
            :placeholder="t('BOOKING_WIDGETS.ANALYTICS.FACEBOOK_PIXEL_PLACEHOLDER')"
            @update:model-value="
              patchAnalyticsProvider('facebook_pixel', 'api_key', $event)
            "
          />
        </div>

        <div class="space-y-3 rounded-lg border border-n-weak p-3">
          <div class="flex items-center justify-between gap-3">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('BOOKING_WIDGETS.ANALYTICS.GOOGLE_TAG') }}
            </span>
            <Switch
              :model-value="analyticsSettings.google_tag.enabled"
              @update:model-value="
                patchAnalyticsProvider('google_tag', 'enabled', $event)
              "
            />
          </div>
          <Input
            v-if="analyticsSettings.google_tag.enabled"
            :model-value="analyticsSettings.google_tag.tag_ids_text"
            :label="t('BOOKING_WIDGETS.ANALYTICS.GOOGLE_TAG_IDS')"
            :placeholder="t('BOOKING_WIDGETS.ANALYTICS.GOOGLE_TAG_PLACEHOLDER')"
            @update:model-value="
              patchAnalyticsProvider('google_tag', 'tag_ids_text', $event)
            "
          />
          <Input
            v-if="analyticsSettings.google_tag.enabled"
            :model-value="analyticsSettings.google_tag.lead_send_to"
            :label="t('BOOKING_WIDGETS.ANALYTICS.LEAD_SEND_TO')"
            :placeholder="t('BOOKING_WIDGETS.ANALYTICS.LEAD_SEND_TO_PLACEHOLDER')"
            @update:model-value="
              patchAnalyticsProvider('google_tag', 'lead_send_to', $event)
            "
          />
        </div>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
        <h3
          class="mb-2 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
        >
          <span class="i-lucide-code size-4 shrink-0 text-n-slate-11" />
          {{ t('BOOKING_WIDGETS.EMBED') }}
        </h3>
        <div class="rounded-lg bg-n-slate-3 p-2.5 dark:bg-n-solid-3">
          <woot-code lang="html" :script="embedSnippet" />
        </div>
      </div>
    </div>
  </section>
</template>
